/**
 * A pergunta da casa — o que era `window.confirm` e `window.prompt`.
 *
 * As caixas do navegador travam a aba inteira, não têm a cara do escritório e,
 * pior, o rótulo do botão é do sistema: "OK" para apagar um fluxo é a mesma
 * palavra que para salvar um nome. Aqui a ação diz o que faz ("Apagar o
 * fluxo"), o que é irreversível fica em terracota e o Esc cancela.
 *
 * A API é imperativa de propósito: quem pergunta é uma AÇÃO
 * (`apagarFluxo`, `novoCaso`), não um componente com estado. Então a caixa vive
 * uma vez na casca e as funções abaixo devolvem promessa:
 *
 *     if (!(await confirmar({ titulo: "Apagar?", perigo: true }))) return;
 *     const nome = await pedirTexto({ titulo: "Nome do cliente" });
 *
 * Sem `<Perguntas/>` montado, tudo responde "cancelou" — nunca "confirmou": uma
 * tela sem a caixa não pode apagar nada por omissão.
 *
 * A caixa é o `Dialog` do shadcn (primitiva do `@base-ui/react`), e não o
 * `AlertDialog`: aqui o clique fora CANCELA, que é o que a mesa promete desde
 * sempre, e o alerta do registro tranca a saída de propósito. O que é
 * irreversível se avisa pela cor do botão e pelo verbo dele, não prendendo a
 * pessoa na caixa.
 */
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Button } from "@/ui/button.tsx";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/ui/dialog.tsx";
import { Input } from "@/ui/input.tsx";
import { Textarea } from "@/ui/textarea.tsx";
import { CampoDeData } from "./CampoDeData.tsx";
import { CAMPO } from "./pele.ts";

type Base = {
  titulo: string;
  texto?: ReactNode;
  /** O rótulo do botão que confirma. Diz a ação, não "OK". */
  acao?: string;
  /** Irreversível: botão em terracota. */
  perigo?: boolean;
};

type Pedido = Base & {
  campo?: { valor: string; convite?: string; linhas?: number; opcional?: boolean; tipo?: string };
  /* Terceiro botão: a outra saída legítima, ao lado de confirmar e cancelar. */
  alternativa?: string;
  responder: (resposta: string | null) => void;
};

let publicar: ((pedido: Pedido | null) => void) | null = null;

function abrir(pedido: Omit<Pedido, "responder">): Promise<string | null> {
  if (!publicar) return Promise.resolve(null);
  return new Promise((responder) => publicar?.({ ...pedido, responder }));
}

/** Sim ou não. Devolve `false` no Esc, no clique fora e no Cancelar. */
export async function confirmar(pedido: Base): Promise<boolean> {
  return (await abrir({ acao: "Confirmar", ...pedido })) !== null;
}

/**
 * Duas saídas boas e uma desistência.
 *
 * Nasceu do pareamento do WhatsApp: "baixar o histórico do celular" e "começar
 * do zero" são as DUAS respostas certas, e nenhuma é o cancelamento — quem
 * fecha a caixa não quis conectar. Com `confirmar`, "começar do zero" teria de
 * virar o botão Cancelar, e a pessoa que aperta Esc por reflexo pareava sem
 * histórico sem ter escolhido isso.
 */
export async function escolher(pedido: Base & { alternativa: string }): Promise<"sim" | "alternativa" | null> {
  const r = await abrir({ acao: "Confirmar", ...pedido });
  return r === null ? null : r === "alternativa" ? "alternativa" : "sim";
}

/**
 * Uma linha de texto. Devolve `null` se cancelou, e nunca string vazia.
 *
 * `opcional` libera o botão com o campo em branco (o telefone do caso novo, que
 * pode entrar depois); sem ele, confirmar em branco não é possível.
 */
export async function pedirTexto(
  pedido: Base & { valor?: string; convite?: string; linhas?: number; opcional?: boolean; tipo?: string },
): Promise<string | null> {
  const { valor = "", convite, linhas, opcional, tipo, ...resto } = pedido;
  const resposta = await abrir({ acao: "Salvar", ...resto, campo: { valor, convite, linhas, opcional, tipo } });
  const limpo = resposta?.trim();
  if (limpo) return limpo;
  /* Campo opcional confirmado em branco é "" (a pessoa disse "sem telefone");
     `null` é só cancelamento. */
  return opcional && resposta !== null ? "" : null;
}

/** Monte uma vez, na casca. É a caixa de toda pergunta da mesa. */
export function Perguntas() {
  const [aberta, setAberta] = useState<{ pedido: Pedido; n: number } | null>(null);

  useEffect(() => {
    publicar = (pedido) =>
      setAberta((atual) => {
        /* Pergunta nova por cima de outra cancela a de baixo: promessa que não
           resolve deixa a ação pendurada para sempre. */
        if (atual && atual.pedido !== pedido) atual.pedido.responder(null);
        return pedido ? { pedido, n: (atual?.n ?? 0) + 1 } : null;
      });
    return () => {
      publicar = null;
    };
  }, []);

  if (!aberta) return null;
  /* `key` pelo contador: é o que zera o texto digitado entre duas perguntas. */
  return <Caixa key={aberta.n} pedido={aberta.pedido} aoFechar={() => setAberta(null)} />;
}

/**
 * A caixa em si, remontada a cada pergunta.
 *
 * Componente separado por causa do `key`: é ele que zera o texto digitado entre
 * duas perguntas seguidas — sem isso a segunda nasceria com a resposta da
 * primeira dentro.
 */
function Caixa({ pedido, aoFechar }: { pedido: Pedido; aoFechar: () => void }) {
  const [texto, setTexto] = useState(pedido.campo?.valor ?? "");

  const responder = (resposta: string | null) => {
    pedido.responder(resposta);
    aoFechar();
  };

  const travado = Boolean(pedido.campo) && !pedido.campo?.opcional && !texto.trim();

  const enviar = (e: FormEvent) => {
    e.preventDefault();
    /* Sem campo, confirmar é qualquer coisa diferente de `null`. */
    if (travado) return;
    /* Com campo opcional em branco a resposta é " ": string vazia é "cancelou",
       e cancelar não foi o que a pessoa fez. */
    responder(pedido.campo ? texto || " " : "sim");
  };

  /* O botão que fecha a decisão tem 44: é o último toque da caixa, e errar ali
     é fazer a outra coisa. `perigo` é destrutivo CHEIO, e não terra — terra é
     "vai ao cliente". */
  const botao = "h-toque rounded-controle px-[18px] text-corpo font-semibold";

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && responder(null)}>
      <DialogContent
        showCloseButton={false}
        /* A pele é a da `Cortina` com faixas (`comuns.jsx`): raio 18, título
           numa faixa com fio embaixo, pé em papel-fundo com fio em cima. A
           pergunta era a única caixa da mesa com outro desenho — raio, botão e
           campo em fundo branco próprios. */
        className="z-[110] w-[min(420px,100%)] gap-0 overflow-hidden rounded-superficie border border-border bg-card p-0 shadow-[0_30px_70px_-20px_color-mix(in_oklab,_var(--color-escuro)_40%,_transparent)] ring-0 sm:max-w-none"
      >
        <form onSubmit={enviar}>
          <div className={pedido.campo ? "px-[22px] pt-5 pb-3.5" : "border-b border-b-border px-[22px] pt-5 pb-3.5"}>
            <DialogTitle className="m-0 font-serif text-titulo leading-tight">{pedido.titulo}</DialogTitle>
            {pedido.texto ? (
              <DialogDescription className="mt-1.5 mb-0 text-corpo leading-[1.55] text-tinta-suave">
                {pedido.texto}
              </DialogDescription>
            ) : null}
          </div>

          {pedido.campo ? (
            <div className="border-b border-b-border px-[22px] pb-4">
              {pedido.campo.linhas ? (
                <Textarea
                  autoFocus
                  rows={pedido.campo.linhas}
                  value={texto}
                  placeholder={pedido.campo.convite}
                  aria-label={pedido.titulo}
                  onChange={(e) => setTexto(e.target.value)}
                  className={`${CAMPO} h-auto min-h-[88px] resize-y py-2.5 leading-[1.5]`}
                />
              ) : pedido.campo.tipo === "date" || pedido.campo.tipo === "datetime-local" ? (
                /* Data é do calendário da casa (`CampoDeData`), nunca do
                   navegador — o nativo falava inglês e "mm/dd/yyyy". */
                <CampoDeData
                  autoFocus
                  rotulo={pedido.titulo}
                  valor={texto}
                  aoMudar={setTexto}
                  comHora={pedido.campo.tipo === "datetime-local"}
                  limpar={Boolean(pedido.campo.opcional)}
                  dica={pedido.campo.convite ?? "dd/mm/aaaa"}
                  className={`${CAMPO} focus-visible:outline-none!`}
                />
              ) : (
                <Input
                  autoFocus
                  type={pedido.campo.tipo ?? "text"}
                  value={texto}
                  placeholder={pedido.campo.convite}
                  aria-label={pedido.titulo}
                  onChange={(e) => setTexto(e.target.value)}
                  className={CAMPO}
                />
              )}
            </div>
          ) : null}

          <DialogFooter className="m-0 flex-row flex-wrap justify-end gap-2 rounded-none border-t-0 bg-papel-fundo px-[22px] py-3.5 max-[759px]:*:flex-1">
            <Button
              type="button"
              variant="outline"
              onClick={() => responder(null)}
              className={`${botao} border-borda-controle bg-card text-tinta-media hover:bg-muted hover:text-foreground`}
            >
              Cancelar
            </Button>
            {pedido.alternativa ? (
              <Button
                type="button"
                variant="outline"
                onClick={() => responder("alternativa")}
                className={`${botao} border-borda-controle bg-card text-tinta-media hover:bg-muted hover:text-foreground`}
              >
                {pedido.alternativa}
              </Button>
            ) : null}
            <Button
              type="submit"
              /* Campo vazio não confirma: era o `prompt` devolvendo "" e a ação
                 gravando um nome em branco. O desabilitado legível (fundo
                 apagado, borda tracejada) é do `index.css`. */
              disabled={travado}
              className={`${botao} ${
                pedido.perigo ? "bg-destructive text-sobre-terra hover:bg-destructive/86" : "bg-tinta text-mesa hover:bg-tinta/86"
              }`}
            >
              {pedido.acao}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
